import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Printer, Sparkles, ShieldAlert, ExternalLink, MessageCircle } from "lucide-react";

const WHATSAPP_URL = "https://chat.whatsapp.com/Gt0Bg2Ui8FkCC0xokywWVE";
import Seo from "@/components/Seo";
import { FadeUp, Eyebrow } from "@/components/Reveal";
import { MarqueeRibbon } from "@/components/MarqueeRibbon";
import { steps } from "@/data/steps";
import { printChecklist } from "@/utils/printChecklist";

const STORAGE_KEY = "hijrah-mom-checklist";

const loadChecked = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

export default function Steps() {
  const [open, setOpen] = useState(steps[0].id);
  const [checked, setChecked] = useState(loadChecked);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
  }, [checked]);

  const total = useMemo(() => steps.reduce((n, s) => n + s.checklist.length, 0), []);
  const done = Object.values(checked).filter(Boolean).length;
  const pct = Math.round((done / total) * 100);

  const toggle = (key) => setChecked((c) => ({ ...c, [key]: !c[key] }));

  return (
    <div data-testid="steps-page" className="pt-24 lg:pt-28 bg-cream min-h-screen grain relative">
      <Seo
        title="The Hijrah Roadmap — Six Chapters from Niyyah to One-Way Ticket | Hijrah Mom"
        siteName="Hijrah Mom"
        description="A step-by-step leaving guide for American Muslim mothers: intention, passports, housing (sell, rent out, or exit your lease), belongings, child custody, and choosing your country and visa."
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-12 lg:py-16">
        <Eyebrow>The American mom's hijrah roadmap</Eyebrow>
        <h1 className="mt-4 font-serif font-bold text-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          Twelve chapters between you <span className="italic text-petal">and the adhan.</span>
        </h1>
        <p className="mt-6 text-sage max-w-2xl leading-relaxed">
          This is the order things actually happen: intention, mindset, passports, housing, the belongings, the custody papers, the destination, schooling, money, mail, taxes, and telling the people you love. Tick items off — your progress is saved privately on this device.
        </p>

        <FadeUp className="mt-10 bg-white rounded-2xl border border-pine/10 p-6 flex flex-col sm:flex-row sm:items-center gap-5 shadow-sm" data-testid="steps-progress">
          <div className="flex-1">
            <div className="flex justify-between items-baseline mb-2">
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-pine">Your progress</span>
              <span className="font-mono text-sm text-petal" data-testid="steps-progress-label">{done}/{total} · {pct}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-blush overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-petal"
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </div>
          <button
            data-testid="steps-print-btn"
            onClick={() => printChecklist(steps, checked)}
            className="inline-flex items-center gap-2 border border-pine/25 text-pine text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-blush-deep transition-colors"
          >
            <Printer size={15} /> Print checklist
          </button>
        </FadeUp>

      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-8 pb-20 lg:pb-28 space-y-5">
        {steps.map((s, i) => {
          const isOpen = open === s.id;
          const stepDone = s.checklist.filter((_, j) => checked[`${s.id}-${j}`]).length;
          return (
            <FadeUp key={s.id} delay={i * 0.05}>
              <div
                id={s.id}
                data-testid={`step-chapter-${s.id}`}
                className={`scroll-mt-28 rounded-2xl border transition-colors duration-300 overflow-hidden ${
                  isOpen ? "bg-white border-petal/30 shadow-lg shadow-petal/10" : "bg-white/70 border-pine/10 hover:border-petal/30"
                }`}
              >
                <button
                  data-testid={`step-toggle-${s.id}`}
                  onClick={() => setOpen(isOpen ? null : s.id)}
                  className="w-full flex items-center gap-5 sm:gap-8 px-6 sm:px-8 py-6 text-left"
                  aria-expanded={isOpen}
                >
                  <span className={`font-serif font-bold text-3xl sm:text-4xl transition-colors ${isOpen ? "text-petal" : "text-outline"}`}>
                    {s.number}
                  </span>
                  <span className="flex-1">
                    <span className="block font-serif font-semibold text-xl sm:text-2xl text-ink">{s.title}</span>
                    <span className="block text-sm text-sage-muted mt-0.5">{s.subtitle} · {stepDone}/{s.checklist.length} done</span>
                  </span>
                  <motion.span animate={{ rotate: isOpen ? 180 : 0 }} className="text-pine/50">
                    <ChevronDown size={20} />
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 sm:px-8 pb-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="space-y-4">
                          {s.body.map((p, j) => (
                            <div key={j} className="space-y-4">
                              <p className="text-ink/85 leading-relaxed text-[15px]">{p}</p>
                              {j === 0 && s.bullets && (
                                <div data-testid={`step-bullets-${s.id}`}>
                                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-pine mb-3">{s.bullets.title}</p>
                                  <ul className="space-y-2">
                                    {s.bullets.items.map((item) => {
                                      const [head, ...rest] = item.split(" — ");
                                      return (
                                        <li key={item} className="flex gap-2.5 text-[15px] text-ink/85 leading-relaxed">
                                          <span className="text-petal mt-0.5">✦</span>
                                          <span><span className="font-semibold text-pine">{head}</span>{rest.length > 0 && ` — ${rest.join(" — ")}`}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ))}
                          {s.stateGuide && (
                            <div className="mt-2 space-y-3" data-testid="state-escape-guide">
                              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-pine">The sticky five — how each state keeps you, and how to leave clean</p>
                              {s.stateGuide.map((st) => (
                                <div key={st.state} className="bg-blush rounded-xl border border-petal/20 p-4" data-testid={`state-card-${st.state.toLowerCase().replace(/\s/g, "-")}`}>
                                  <p className="font-serif font-semibold text-lg text-pine">{st.state}</p>
                                  <p className="text-xs text-petal-dark mt-0.5 font-medium">{st.trap}</p>
                                  <p className="text-sm text-ink/80 mt-2 leading-relaxed"><span className="font-semibold text-pine">Cut it clean: </span>{st.fix}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          {s.links && (
                            <div className="mt-2 space-y-2" data-testid={`step-links-${s.id}`}>
                              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-pine">Official government links</p>
                              {s.links.map((l) => (
                                <a
                                  key={l.url}
                                  href={l.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`step-link-${s.id}-${l.url.replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}`}
                                  className="group flex items-start justify-between gap-3 bg-white border border-pine/15 rounded-xl px-4 py-3 hover:border-pine hover:bg-pine/5 transition-colors"
                                >
                                  <span>
                                    <span className="block text-sm font-semibold text-pine group-hover:text-pine-dark">{l.label}</span>
                                    <span className="block text-xs text-sage mt-0.5">{l.note}</span>
                                  </span>
                                  <ExternalLink size={15} className="text-petal shrink-0 mt-0.5" />
                                </a>
                              ))}
                            </div>
                          )}
                          <div className="bg-blush rounded-xl border border-petal/25 p-5">
                            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal flex items-center gap-1.5 mb-2">
                              <Sparkles size={12} /> Mom's pro-tip
                            </p>
                            <p className="font-serif italic text-ink/85 leading-relaxed">{s.momTip}</p>
                          </div>
                        </div>
                        <div>
                          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-pine mb-4">Chapter checklist</p>
                          <ul className="space-y-3">
                            {s.checklist.map((item, j) => {
                              const key = `${s.id}-${j}`;
                              const isDone = !!checked[key];
                              return (
                                <li key={key}>
                                  <button
                                    data-testid={`check-${key}`}
                                    onClick={() => toggle(key)}
                                    className="w-full flex items-start gap-3 text-left group"
                                    aria-pressed={isDone}
                                  >
                                    <span
                                      className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors duration-200 ${
                                        isDone ? "bg-petal border-petal text-white" : "border-pine/25 group-hover:border-petal"
                                      }`}
                                    >
                                      {isDone && (
                                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 6.5L4.5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                                      )}
                                    </span>
                                    <span className={`text-sm leading-relaxed transition-colors ${isDone ? "line-through text-sage-muted" : "text-ink/85"}`}>
                                      {item}
                                    </span>
                                  </button>
                                  {item.includes("WhatsApp group") && (
                                    <a
                                      href={WHATSAPP_URL}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      data-testid="checklist-whatsapp-link"
                                      className="ml-8 mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#25D366] rounded-full px-3 py-1.5 hover:bg-[#1ebe5b] transition-colors"
                                    >
                                      <MessageCircle size={13} /> Join on WhatsApp
                                    </a>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                          {s.id === "destination" && (
                          <div className="mt-6 bg-blush border border-petal/30 rounded-2xl p-5 lg:p-6 flex gap-4" data-testid="visa-scam-warning">
          <span className="w-11 h-11 rounded-full bg-petal text-white flex items-center justify-center shrink-0"><ShieldAlert size={20} /></span>
          <div>
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-petal">Read this before you pay anyone</p>
            <h3 className="mt-1.5 font-serif font-semibold text-ink text-xl">Beware of fake visa sites and "agents."</h3>
            <p className="mt-2 text-sm text-ink/85 leading-relaxed">
              Sisters have lost thousands to lookalike websites, Instagram "visa consultants," and third-party portals that charge triple for a form you can file yourself. Apply <strong>only</strong> through the official government portal or embassy of the country (look for a <span className="font-mono">.gov</span> or official national domain), and never pay by gift card, crypto, or wire to a personal account.
            </p>
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-ink/85">
              <li>· Google the exact URL + "scam" before entering card details</li>
              <li>· Real e-visas rarely cost more than the embassy fee listed on this site</li>
              <li>· No legitimate office "guarantees" approval or asks for your passport by DM</li>
              <li>· When in doubt, call the embassy in Washington, D.C. and ask</li>
            </ul>
          </div>
                          </div>
                          )}
                        </div>
                      </div>
                      {s.options && (
                            <div className="px-6 sm:px-8 pb-8 -mt-2 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="education-options">
                              {s.options.map((o) => (
                                <div key={o.name} className="bg-white border border-pine/15 rounded-xl p-5" data-testid={`edu-option-${o.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <p className="font-serif font-semibold text-xl text-pine">{o.name}</p>
                                    <span className="text-xs font-mono px-2.5 py-1 rounded-full bg-petal/15 text-petal-dark border border-petal/25">{o.cost}</span>
                                  </div>
                                  <p className="text-xs text-sage mt-1"><span className="font-semibold text-pine">Best for: </span>{o.bestFor}</p>
                                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <ul className="space-y-1.5">
                                      {o.pros.map((x) => <li key={x} className="text-sm text-ink/85 flex gap-2"><span className="text-pine font-bold">+</span>{x}</li>)}
                                    </ul>
                                    <ul className="space-y-1.5">
                                      {o.cons.map((x) => <li key={x} className="text-sm text-ink/85 flex gap-2"><span className="text-petal font-bold">–</span>{x}</li>)}
                                    </ul>
                                  </div>
                                  <p className="mt-3 font-serif italic text-sm text-ink/80 border-t border-pine/10 pt-3">{o.momNote}</p>
                                </div>
                              ))}
                            </div>
                          )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </FadeUp>
          );
        })}

        <FadeUp className="bg-pine-ink text-cream rounded-3xl p-8 lg:p-12 grain relative" data-testid="steps-disclaimer">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-petal-soft">Before you go</p>
          <p className="mt-4 font-serif italic text-xl lg:text-2xl leading-relaxed max-w-3xl">
            This roadmap is experience, not legal advice. Custody and immigration questions deserve a licensed attorney and an accredited embassy — and every big decision deserves istikhara.
          </p>
        </FadeUp>
      </section>

      <MarqueeRibbon />
    </div>
  );
}
